/**
 * Phone / SMS sign-in.
 *
 * Two-step UX:
 *   1. User enters E.164 phone → invisible reCAPTCHA → Firebase
 *      sends a 6-digit SMS code.
 *   2. User enters the code → Firebase verifies + returns a User.
 *
 * The reCAPTCHA is configured to live on auth.horuswallet.com (Horus
 * registers the domain with reCAPTCHA Enterprise), so partners avoid
 * having to do their own reCAPTCHA setup per domain.
 */

import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  ConfirmationResult,
} from 'firebase/auth';
import { getFirebaseAuth } from '../firebase';
import { PostMessageContext } from '../postmessage';
import {
  el,
  mount,
  postCancel,
  postError,
  postReady,
  postSuccess,
  humanizeFirebaseError,
  setBusy,
  getQueryParam,
} from '../util';

export function renderPhoneFlow(ctx: PostMessageContext): void {
  let confirmation: ConfirmationResult | null = null;
  // Hold the verifier across submit attempts so we can clear() between
  // tries. A second `new RecaptchaVerifier(..., 'recaptcha-container')`
  // on the same DOM node throws `auth/internal-error` with no useful
  // detail — Firebase's container guard treats the existing widget as
  // a duplicate. Reuse-or-clear is the supported pattern.
  let verifier: RecaptchaVerifier | null = null;

  // ── Step 1 — phone form ────────────────────────────────────────
  const titleEl = el('h1', {}, ['Sign in with phone']);
  const subtitleEl = el('p', { class: 'subtitle' }, [
    'We’ll text a 6-digit verification code.',
  ]);
  const errorBox = el('p', { 'data-error': 'true', class: 'error', style: 'display:none' });

  const phoneInput = el('input', {
    type: 'tel',
    autocomplete: 'tel',
    placeholder: '+15551234567',
    pattern: '^\\+[1-9]\\d{1,14}$',
    required: true,
  }) as HTMLInputElement;
  const prefilledPhone = getQueryParam('phone');
  if (prefilledPhone) phoneInput.value = prefilledPhone;

  const submit = el('button', { class: 'primary', type: 'submit' }, [
    'Send code',
  ]) as HTMLButtonElement;
  const cancel = el('button', { type: 'button' }, ['Cancel']);
  cancel.addEventListener('click', () => postCancel(ctx));

  // Invisible reCAPTCHA target — Firebase fills this div.
  const recaptchaTarget = el('div', { id: 'recaptcha-container' });

  const phoneForm = el('form', {}, [
    el('label', {}, ['Phone (E.164)']),
    phoneInput,
    submit,
    cancel,
    recaptchaTarget,
  ]);

  phoneForm.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    errorBox.style.display = 'none';
    setBusy(true);
    // Two-stage so we can attribute failures correctly — `signInWith…`
    // wraps EVERYTHING (including reCAPTCHA load failures, blocked
    // domains, etc.) as `auth/internal-error` with no extra info.
    // Splitting render() out lets verifier-construction errors carry
    // their own code/message all the way to the partner.
    let stage: 'recaptcha' | 'sms' = 'recaptcha';
    try {
      const auth = getFirebaseAuth();
      // Tear down any prior widget before re-creating (resubmit path).
      if (verifier) {
        try { verifier.clear(); } catch { /* widget may be detached already */ }
        verifier = null;
      }
      verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
      });
      // Explicit render(): forces the reCAPTCHA script to download +
      // initialize NOW, so failures (bad domain, blocked script, App
      // Check rejection) surface here with their own error rather than
      // getting laundered into `auth/internal-error` inside the SMS
      // request below.
      await verifier.render();

      stage = 'sms';
      confirmation = await signInWithPhoneNumber(auth, phoneInput.value, verifier);
      renderCodeStep();
    } catch (err) {
      // Phone-auth errors are notoriously opaque (Firebase wraps almost
      // everything as `auth/internal-error`). Dump the raw error to the
      // popup's console so partners can pinpoint the root cause when
      // they hit one — and pass through more detail in the surfaced
      // message than the generic humanizer would.
      const rawCode = (err as { code?: string } | null)?.code ?? 'auth/unknown';
      const rawMessage = (err as { message?: string } | null)?.message ?? '';
      const serverResponse = (err as { customData?: { serverResponse?: unknown } } | null)
        ?.customData?.serverResponse;
      const authForLog = getFirebaseAuth();
      // eslint-disable-next-line no-console
      console.error('[phone-auth] signInWithPhoneNumber failed:', {
        stage,
        code: rawCode,
        message: rawMessage,
        serverResponse,
        // Helpful breadcrumbs for diagnosing infrastructure issues —
        // these don't include secrets, they're the same values the
        // partner already knows.
        authDomain: authForLog.app.options.authDomain,
        projectId: authForLog.app.options.projectId,
        currentOrigin: window.location.origin,
      });

      // If reCAPTCHA itself failed to render, clear the broken widget
      // so the next attempt gets a fresh one rather than colliding.
      if (verifier) {
        try { verifier.clear(); } catch { /* ignore */ }
        verifier = null;
      }

      const { code, message } = humanizeFirebaseError(err);
      // For auth/internal-error specifically, suffix actionable guidance
      // since the underlying cause is almost always a config issue
      // (CSP, Firebase Console, billing) rather than a code bug. The
      // hint changes based on which stage failed.
      //
      // Order of likelihood (learned the hard way):
      //   recaptcha stage —
      //     1. Auth-page CSP is missing `www.google.com` /
      //        `www.gstatic.com` in script-src so reCAPTCHA's
      //        api.js can't load. Check the console for "Loading
      //        the script ... violates ... Content Security Policy".
      //     2. `${origin}` not in Firebase Authorized domains.
      //     3. reCAPTCHA Enterprise / App Check is enforced but
      //        no site key is registered for this domain.
      //   sms stage —
      //     1. Phone provider disabled in Firebase Console.
      //     2. Project on Spark (free) plan without Blaze billing.
      //     3. SMS region policy rejecting the country code.
      const internalHint =
        stage === 'recaptcha'
          ? `Check the console for a CSP violation on www.google.com/recaptcha/api.js — if present, the auth-page CSP needs script-src updated. Otherwise: ${window.location.origin} may be missing from Firebase Authorized domains, or reCAPTCHA Enterprise / App Check is blocking this origin.`
          : `Phone provider may not be enabled in Firebase Console → Auth → Sign-in method, or the project is on Spark (free) plan without Blaze billing for SMS. Open the browser console for the raw error.`;
      const surfaced =
        code === 'auth/internal-error'
          ? `${message} (Most likely cause: ${internalHint})`
          : message;
      errorBox.textContent = surfaced;
      errorBox.style.display = 'block';
      postError(ctx, code, surfaced);
    } finally {
      setBusy(false);
    }
  });

  // ── Step 2 — code entry ────────────────────────────────────────
  const renderCodeStep = () => {
    const codeError = el('p', { 'data-error': 'true', class: 'error', style: 'display:none' });
    const codeInput = el('input', {
      type: 'text',
      inputmode: 'numeric',
      autocomplete: 'one-time-code',
      placeholder: '6-digit code',
      pattern: '^\\d{6}$',
      maxlength: 6,
      required: true,
    }) as HTMLInputElement;
    const verify = el('button', { class: 'primary', type: 'submit' }, [
      'Verify',
    ]) as HTMLButtonElement;
    const back = el('button', { type: 'button' }, ['Use a different number']);
    back.addEventListener('click', () => renderPhoneFlow(ctx));

    const form = el('form', {}, [
      el('label', {}, ['Verification code']),
      codeInput,
      verify,
      back,
    ]);

    form.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      codeError.style.display = 'none';
      setBusy(true);
      try {
        if (!confirmation) {
          throw new Error('Confirmation flow lost — restart the sign-in.');
        }
        const result = await confirmation.confirm(codeInput.value);
        await postSuccess(ctx, result.user, 'phone');
      } catch (err) {
        const { code, message } = humanizeFirebaseError(err);
        codeError.textContent = message;
        codeError.style.display = 'block';
        postError(ctx, code, message);
      } finally {
        setBusy(false);
      }
    });

    mount(
      el('div', {}, [
        el('h1', {}, ['Enter the code']),
        el('p', { class: 'subtitle' }, [`We sent a 6-digit code to ${phoneInput.value}.`]),
        codeError,
        form,
      ])
    );
  };

  mount(el('div', {}, [titleEl, subtitleEl, errorBox, phoneForm]));
  postReady(ctx);
}
